import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ConnectedProperty, ViewService } from '@vendasta/google-analytics';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { format, parse, subDays } from 'date-fns';
import { Observable, Subscription, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { FeatureFlagService } from '../../core/feature-flag.service';
import { Site, SiteService } from '../../core/site';
import { DateRangeSelectorComponent } from '../date-range-selector';
import {
  AvailableDateRangeOptions,
  DateRangeOption,
  DateRangeSelectorService,
} from '../date-range-selector/date-range-selector.service';
import { CustomOverlayService } from '../ga-metrics';
import { CustomOverlayRef } from '../ga-metrics/custom-overlay-ref';
import { AnalyticsGraph, AnalyticsGraphDefinition, ExtractedAnalyticsData } from './analytics.interfaces';
import { AnalyticsService } from './analytics.service';

@Component({
  selector: 'app-analytics',
  templateUrl: './analytics.component.html',
  styleUrls: ['./analytics.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AnalyticsComponent implements OnInit, OnDestroy {
  @ViewChild('dateSelector') dateSelectorElementRef;

  siteId: string;
  subscriptions: Subscription[] = [];
  partnerId: string;
  private loadDefaultGA4GraphFeature = 'load_default_ga4_graph';

  site$: Observable<Site>;
  connectGALink$: Observable<string>;
  connectGALinkLoading$: Observable<boolean>;
  connectGALinkError$: Observable<boolean>;
  ga4View$: Observable<ConnectedProperty>;
  gaViewLoading$: Observable<boolean>;
  gaViewError$: Observable<boolean>;
  ga4ViewError$: Observable<boolean>;
  gaViewCollectionRunning$: Observable<boolean>;
  gaViewNotFound$: Observable<boolean>;
  ga4ViewNotFound$: Observable<boolean>;

  legacyDefaultViewId = '';
  loadingLegacy = true;
  selectedStartDate: Date = subDays(new Date(), 30);
  selectedEndDate: Date = subDays(new Date(), 1);
  selectedTimeRange$: Observable<DateRangeOption>;
  dateSelectorModalRef: CustomOverlayRef;
  dateSelectorDate$: Observable<[Date, Date]>;
  isGaEnabled = false;
  gaImgUrl = 'https://vstatic-prod.apigateway.co/wsp-portal-client.9223372035179671822.prod/assets/GoogleAnalytics.svg';
  settingsUrl: string;

  // your IDE might say createGoogleAnalyticsAccount is not used, but it is part of an <href> in the template
  createGoogleAnalyticsAccount = 'https://marketingplatform.google.com/about/analytics/';

  lineGraphDefinitions: AnalyticsGraphDefinition = {
    dimension: 'ga:date',
    type: 'line',
    graphs: [
      {
        statName: 'ANALYTICS.ANALYTICS_PANEL.LABELS.TOTAL_VISITS.TITLE',
        metrics: ['ga:sessions', 'ga:newUsers'],
        format: 'number',
        type: 'line',
        statMetric: 'ga:sessions',
        formula: function (): void {
          const totalVisits = this.data[0];
          const newVisits = this.data[1];
          const returnVisits = [];
          for (let n = 0; n < totalVisits.length; n += 1) {
            const totalPoint = totalVisits[n];
            const newPoint = newVisits[n];
            const xValue = totalPoint[0];
            const yValue = totalPoint[1] - newPoint[1];
            returnVisits.push([xValue, yValue]);
          }
          this.data.push(returnVisits);
        },
        labels: [
          'ANALYTICS.ANALYTICS_PANEL.LABELS.TOTAL_VISITS.LABELS.TOTAL_VISITS',
          'ANALYTICS.ANALYTICS_PANEL.LABELS.TOTAL_VISITS.LABELS.NEW_VISITS',
          'ANALYTICS.ANALYTICS_PANEL.LABELS.TOTAL_VISITS.LABELS.RETURNING_VISITS',
        ],
      },
    ],
  };
  areaGraphDefinitions: AnalyticsGraphDefinition = {
    dimension: 'ga:date',
    type: 'line',
    graphs: [
      {
        statName: 'ANALYTICS.ANALYTICS_PANEL.LABELS.TOTAL_VISITORS',
        metrics: ['ga:users'],
        format: 'number',
        type: 'line',
      },
      {
        statName: 'ANALYTICS.ANALYTICS_PANEL.LABELS.PAGE_VIEWS',
        metrics: ['ga:pageviews'],
        format: 'number',
        type: 'line',
      },
      {
        statName: 'ANALYTICS.ANALYTICS_PANEL.LABELS.PAGES_SESSION',
        metrics: ['ga:pageviewsPerSession'],
        format: 'number',
        type: 'line',
      },
      {
        statName: 'ANALYTICS.ANALYTICS_PANEL.LABELS.BOUNCE_RATE',
        metrics: ['ga:bounceRate'],
        format: 'percent',
        type: 'line',
      },
      {
        statName: 'ANALYTICS.ANALYTICS_PANEL.LABELS.AVERAGE_TIME_ON_WEBSITE',
        metrics: ['ga:avgSessionDuration'],
        format: 'time',
        type: 'line',
      },
      {
        statName: 'ANALYTICS.ANALYTICS_PANEL.LABELS.PERCENT_NEW_VISITS',
        metrics: ['ga:percentNewSessions'],
        format: 'percent',
        type: 'line',
      },
    ],
  };
  pieGraphDefinition: AnalyticsGraphDefinition = {
    dimension: 'ga:channelGrouping',
    type: 'pie',
    filters: 'ga:channelGrouping!=/',
    sort: '-ga:sessions',
    maxResults: '4',
    graphs: [
      {
        statName: 'ANALYTICS.ANALYTICS_PANEL.LABELS.TRAFFIC_SOURCE',
        metrics: ['ga:sessions'],
        format: 'number',
        type: 'pie',
        filters: 'ga:channelGrouping!=/',
        sort: '-ga:sessions',
        maxResults: '4',
      },
    ],
  };
  barGraphDefinition: AnalyticsGraphDefinition = {
    dimension: 'ga:source',
    type: 'bar',
    filters: 'ga:source!=/;ga:source!=(direct)',
    sort: '-ga:sessions',
    maxResults: '4',
    graphs: [
      {
        statName: 'ANALYTICS.ANALYTICS_PANEL.LABELS.TOP_REFERRAL_SOURCES',
        metrics: ['ga:sessions'],
        format: 'number',
        type: 'bar',
        filters: 'ga:source!=/;ga:source!=(direct)',
        sort: '-ga:sessions',
        maxResults: '4',
      },
    ],
  };

  constructor(
    private analyticsService: AnalyticsService,
    private translateService: TranslateService,
    protected siteService: SiteService,
    private dateRangeSelectorService: DateRangeSelectorService,
    private gaViewService: ViewService,
    private snowplow: ProductAnalyticsService,
    private customOverlayService: CustomOverlayService,
    private http: HttpClient,
    private alertService: SnackbarService,
    private featureFlag: FeatureFlagService,
  ) {
    this.siteId = this.siteService.siteId;
    this.site$ = this.siteService.site$;
    this.connectGALink$ = this.analyticsService.connectGALink$;
    this.connectGALinkLoading$ = this.analyticsService.connectGALinkLoading$;
    this.connectGALinkError$ = this.analyticsService.connectGALinkError$;
    this.ga4View$ = this.analyticsService.ga4View$;
    this.gaViewLoading$ = this.analyticsService.gaViewLoading$;
    this.gaViewError$ = this.analyticsService.gaViewError$;
    this.ga4ViewError$ = this.analyticsService.ga4ViewError$;
    this.gaViewCollectionRunning$ = this.analyticsService.gaViewCollectionRunning$;
    this.gaViewNotFound$ = this.analyticsService.gaViewNotFound$;
    this.ga4ViewNotFound$ = this.analyticsService.ga4ViewNotFound$;
    this.dateSelectorDate$ = this.dateRangeSelectorService.dateRange$;
    this.selectedTimeRange$ = this.dateRangeSelectorService.timeRange$.pipe(
      map((tr) => {
        return AvailableDateRangeOptions.find((t) => t.key === tr);
      }),
    );
  }

  ngOnInit(): void {
    this.settingsUrl = '/site/' + this.siteId + '/advanced';
    this.analyticsService.getGaIsEnabledState(this.siteId).subscribe();
    this.analyticsService.isGaEnabled.subscribe((data) => {
      this.isGaEnabled = data;
    });

    //feature flag for Google analytics 4 connector

    this.site$.subscribe((data) => {
      this.partnerId = data.partnerId;
      // this.loadDefaultGA4Feature();
    });

    /************   Changed chart library to Highcharts, so this is no longer needed  ************/
    // this.subscriptions.push(
    //   this.analyticsService.ga4ViewNotFound$.subscribe((GA4ViewNotFound) => {
    //     if (GA4ViewNotFound) {
    //       this.subscriptions.push(
    //         this.analyticsService.gaViewNotFound$.subscribe((GAViewNotFound) => {
    //           if (GAViewNotFound) {
    //             this.analyticsService.authorize(this.siteId, (legacyDefaultViewId: string) => {
    //               this.legacyDefaultViewId = legacyDefaultViewId;
    //               this.loadLegacyData();
    //             });
    //           }
    //         }),
    //       );
    //     }
    //   }),
    // );
  }

  /**
   * Queries Google Analytics for the view associated with the site and constructs the associated graphs. Notice that
   * this runs only if the site/account does not have a Google Analytics view connected, which avoids calling the
   * official Google Analytics API
   * @param graphDefinition
   */
  loadDataForGraphDefinition(graphDefinition: AnalyticsGraphDefinition): void {
    const lineDimension = graphDefinition.dimension;
    const lineMetrics = this.getLineMetrics(graphDefinition);

    this.analyticsService
      .buildQuery(this.getQueryOptions(lineMetrics, lineDimension, graphDefinition))
      .pipe(switchMap((res) => this.extractData(res, graphDefinition.type)))
      .subscribe(
        (data) => {
          try {
            this.renderGraphs(data, graphDefinition);
          } catch {
            this.displayError('There was an issue trying to load the analytics data. Please try again.');
          }
        },
        () => {
          this.displayError('There was an issue trying to load the analytics data. Please try again.');
        },
      );
  }

  /**
   * Loads the legacy data associated with querying Google Analytics for a specific view of a site.
   */
  loadLegacyData(): void {
    this.loadingLegacy = true;
    for (const graph of this.lineGraphDefinitions.graphs) {
      const translatedLabels = [];
      for (const label of graph.labels) {
        translatedLabels.push(this.translateService.instant(label));
      }
      graph.labels = translatedLabels;
    }
    this.loadDataForGraphDefinition(this.areaGraphDefinitions);
    this.loadDataForGraphDefinition(this.lineGraphDefinitions);
    this.loadDataForGraphDefinition(this.pieGraphDefinition);
    this.loadDataForGraphDefinition(this.barGraphDefinition);
    this.loadingLegacy = false;
  }

  /**
   * Invokes the rendering functions that display the legacy Google Analytics data of a site
   * @param data - the response from calling the Google Analytics API for querying data for a view
   * @param graphDefinition
   */
  renderGraphs(data: any, graphDefinition: AnalyticsGraphDefinition): void {
    const dataSet = data.data;
    const dataTotals = data.totals;
    let start = 0;
    for (const graph of graphDefinition.graphs) {
      const end = start + graph.metrics.length;
      const graphData = dataSet.slice(start, end);
      this.renderGraphStats(dataTotals, graph);
      this.renderGraphData(graphData, graph);
      this.renderGraphFormula(graph);
      start = end;
    }
  }

  /**
   * Sets the stat value of the given graph to the associated data totals
   * @param dataTotals - collection of total data points received from Google Analytics
   * @param graph - the analytics graph on which to set the total
   */
  renderGraphStats(dataTotals: any, graph: AnalyticsGraph): void {
    const statMetric = graph.statMetric || graph.metrics[0];
    graph.statValue = dataTotals[statMetric];
  }

  /**
   * Uses the given data set from Google Analytics to push data associated with a graph onto the corresponding graph
   * @param dataSet - collection of data received from Google Analytics
   * @param graph - graph onto which to push data
   */
  renderGraphData(dataSet: any, graph: AnalyticsGraph): void {
    graph.data = [];
    for (let m = 0; m < graph.metrics.length; m += 1) {
      const data = dataSet[m] || null;
      if (data) {
        graph.data.push(data);
      }
    }
    if (graph.data.length < 1) {
      graph.data = null;
    }
  }

  /**
   * Runs a graph's formula, which constructs the data points according to the specifications of the graph to render.
   * See the graph definitions for more details on what each formula does
   * @param graph - the analytics graph whose formula should run
   */
  renderGraphFormula(graph: AnalyticsGraph): void {
    if (graph.formula) {
      graph.formula();
    }
  }

  /**
   * Performs a reduce on the line metrics of a graph to return a final array that contains all the metrics, rather than
   * leave the graph definition with multiple line metrics
   * @param graphDefinition
   */
  getLineMetrics(graphDefinition: AnalyticsGraphDefinition): string {
    return graphDefinition.graphs
      .reduce((lineMetrics: string[], graph: AnalyticsGraph) => {
        lineMetrics = lineMetrics.concat(graph.metrics);
        return lineMetrics;
      }, [])
      .join(',');
  }

  /**
   * Constructs and returns the query that is used for receiving data from Google Analytics
   * @param metrics - the specific metrics to query Google Analytics for
   * @param dimension - the dimension to query
   * @param graphDefinition - the graph definition that contains the filters associated with the query that is
   * constructed
   *
   * For more information on what each parameter of the query does, see https://developers.google.com/analytics/devguides/reporting/core/v3/reference
   */
  getQueryOptions(metrics: string, dimension: string, graphDefinition: AnalyticsGraphDefinition): any {
    const query: any = {
      ids: this.legacyDefaultViewId,
      'start-date': format(this.selectedStartDate, 'yyyy-MM-dd'),
      'end-date': format(this.selectedEndDate, 'yyyy-MM-dd'),
      metrics: metrics,
      dimensions: dimension,
      samplingLevel: 'HIGHER_PRECISION',
      quotaUser: this.siteId.substring(0, 40),
    };
    let gaFilter = `ga:dimension1==${this.siteId}`;
    if (graphDefinition.filters) {
      gaFilter += ';' + graphDefinition.filters;
    }
    query.filters = gaFilter;

    if (graphDefinition.sort) {
      query.sort = graphDefinition.sort;
    }

    /* tslint:disable:no-string-literal */
    if (graphDefinition.maxResults) {
      query['max-results'] = graphDefinition.maxResults;
    }
    /* tslint:enable:no-string-literal */
    return query;
  }

  /**
   * Extracts the data returned by the Google Analytics API
   * @param result - the result of querying Google Analytics
   * @param type - the
   */
  public extractDataObject(result: any, type: string): ExtractedAnalyticsData {
    if (result.totalResults < 1) {
      return {
        data: [],
        metrics: [],
        totals: [],
      };
    }
    const data = [];
    const metrics = result.query.metrics;
    const totals = result.totalsForAllResults;
    for (const row of result.rows) {
      const xValue = row.shift();
      for (let i = 0; i < row.length; i++) {
        const datum = row[i];
        if (!data[i]) {
          data[i] = [];
        }
        switch (type) {
          case 'line': {
            const date = parse(xValue, 'yyyyMMdd', new Date());
            data[i].push([date.getTime(), parseInt(datum, 10)]);
            break;
          }
          case 'pie':
            data[i].push({
              name: xValue,
              y: parseInt(datum, 10),
            });
            break;
          case 'bar': {
            const value = (parseFloat(datum) / parseFloat(result.totalsForAllResults['ga:sessions'])) * 100;
            data[i].push({
              name: xValue,
              data: [value],
            });
            break;
          }
        }
      }
    }
    return {
      data: data,
      metrics: metrics,
      totals: totals,
    };
  }

  /**
   * Constructs and observable of ExtractedAnalyticsData using the final result and type of query returned
   * @param result - the result of the Google Analytics query
   * @param type - the type of graph to extract data for ('line' | 'pie' | 'bar')
   */
  extractData(result: any, type: string): Observable<ExtractedAnalyticsData> {
    return of(this.extractDataObject(result, type));
  }

  /**
   * Opens the date selector in the given origin, which comes from the template
   * @param origin - origin on the template on which to open the selector
   */
  openDateSelector(origin: any): void {
    if (!this.dateSelectorModalRef) {
      this.dateSelectorModalRef = this.customOverlayService.open<any>({
        component: DateRangeSelectorComponent,
        origin,
        positions: ['bottom-right'],
        scrollStrategy: 'reposition',
        panelClass: 'mobile-fullscreen-overlay',
        hasBackdrop: true,
      });

      this.dateSelectorModalRef.afterClosed$.subscribe({
        next: () => {
          this.dateSelectorModalRef = undefined;
        },
      });
    }
  }

  /**
   * Displays a snackbar with the given error
   * @param error - the error message to display
   */
  displayError(error: string): void {
    this.alertService.openErrorSnack(error, this.translateService.instant('ALERTS.GENERAL.DISMISS'));
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  // /**  Hide or show the default GA4 graph based on the feature flag ***/
  // loadDefaultGA4Feature(): void {
  //   if (this.partnerId) {
  //     this.google_analyticsGA4DefaultGraph$ = this.featureFlag
  //       .isFeatureEnabled(this.partnerId, this.loadDefaultGA4GraphFeature, '')
  //       .pipe((r) => {
  //         return r;
  //       });
  //   }
  // }
}
